import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useSession } from './session.js';

/** 按模块配置品牌的面:学习平台(学生端 / 家长端)· 考试端 · 大赛门户 */
export type BrandSurface = 'learning' | 'exam' | 'contest';
export interface BrandFaceView {
  displayName: string;
  themeColor: string;
  logoUrl: string | null;
}

export interface SiteTenant {
  /** 服务端按请求地址(站点码头 / 子域名 / 机构自有域名)解析出的站点码;平台站点没有 */
  code?: string;
  name?: string;
  /** 租户类型(training / channel / public_school / organizer);平台站点没有 */
  type?: string;
  status: string;
  statusLabel: string;
  expiresAt?: string | null;
  graceUntil?: string | null;
  /** 机构品牌(Logo / 欢迎语只给审核通过的;平台站点没有) */
  brand?: {
    displayName: string;
    themeColor: string;
    logoUrl: string | null;
    accentColor?: string;
    mode?: 'unified' | 'per_module';
    faces?: Partial<Record<BrandSurface, BrandFaceView>>;
    footer?: { company: string | null; phone: string | null; icp: string | null; police: string | null };
    /** 生效的白标(模块开着才算):site 学生端 / 家长端 / 门户;admin 后台 */
    whiteLabel?: { site: boolean; admin: boolean };
    welcome?: string | null;
  };
  /** 生效 / 到期只读的模块(平台站点没有 = 不限) */
  modules?: string[];
  expiredModules?: string[];
  /** 到期只读模块的名称与到期时间(该模块的后台页面顶部挂只读横幅,B24-3) */
  moduleExpiry?: { key: string; label: string; until: string | null }[];
  /** 机构 / 学校还没走完也没跳过开通引导 */
  onboardingPending?: boolean;
  /** 迁移到私有化部署的标记(二期):迁移中 / 已迁完的保留期,机构只读 */
  transfer?: { state: 'migrating' | 'migrated'; retainUntil: string | null } | null;
}

/** 部署形态(二期「私有化部署」):saas = 平台多租户;private = 客户机房里的单机构实例 */
export interface DeployInfo {
  mode: 'saas' | 'private';
  version: string;
  /** 私有化实例是否已激活(SaaS 为 null) */
  activated: boolean | null;
}

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('zh-CN') : '');

/** 把主题色 / 强调色写到 <html data-theme / data-accent>(styles/themes.css 按它换色);默认值去掉 */
function applyColors(theme: string | undefined, accent: string | undefined) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme && theme !== 'blue') root.dataset['theme'] = theme;
  else delete root.dataset['theme'];
  if (accent && accent !== 'amber') root.dataset['accent'] = accent;
  else delete root.dataset['accent'];
}

/** 页面属于哪个面:考试端 /exam、大赛门户 /c/,后台用统一品牌,其余是学习平台 */
export function surfaceOf(path: string): BrandSurface | null {
  if (path.startsWith('/admin')) return null;
  if (path === '/exam' || path.startsWith('/exam/')) return 'exam';
  if (path.startsWith('/c/')) return 'contest';
  return 'learning';
}

/**
 * 当前站点机构的状态(设计图 24「租户只读横幅」)、品牌与模块:每次打开应用查一次;
 * 接口回 T0101 / T0103 / T0104 时、改了品牌或开通了模块后也会刷新。横幅文案按状态给:到期宽限 / 超配额 / 冻结。
 */
export const useTenantStatus = defineStore('tenantStatus', () => {
  const info = ref<SiteTenant | null>(null);
  const deploy = ref<DeployInfo | null>(null);
  const surface = ref<BrandSurface | null>(null);
  /** 第一次查站点信息有结果了(成功或失败都算)。还没有结果时,页面不知道这是不是机构站点、开没开白标 */
  const settled = ref(false);
  let loaded = false;
  let inflight: Promise<void> | null = null;

  function faceOf(s: BrandSurface | null): BrandFaceView | null {
    const b = info.value?.brand;
    if (!b) return null;
    return (
      (s && b.mode === 'per_module' ? b.faces?.[s] : null) ?? {
        displayName: b.displayName,
        themeColor: b.themeColor,
        logoUrl: b.logoUrl,
      }
    );
  }
  function paint() {
    const b = info.value?.brand;
    applyColors(faceOf(surface.value)?.themeColor, b?.accentColor);
  }
  /** 路由切换时按页面所属的面换主题色(按模块配置品牌时考试端 / 门户可以是别的颜色) */
  function applySurface(s: BrandSurface | null) {
    surface.value = s;
    paint();
  }

  async function load(force = false): Promise<void> {
    if (loaded && !force) return inflight ?? Promise.resolve();
    loaded = true;
    inflight = (async () => {
      try {
        const session = useSession();
        // 部署形态和站点状态一起取(菜单按它出「私有化部署」还是「授权与升级」);取不到按 SaaS
        const [site, mode] = await Promise.all([
          session.api.get<SiteTenant>('/v1/site/tenant'),
          session.api.get<DeployInfo>('/v1/site/deploy').catch(() => null),
        ]);
        info.value = site;
        deploy.value = mode;
        // 机构自有域名:站点码由服务端按域名认出来,之后的请求带上(A18-5)
        session.setResolvedSite(info.value.code);
        paint();
      } catch {
        /* 拿不到就不显示横幅、不换品牌 */
      } finally {
        settled.value = true;
      }
    })();
    return inflight;
  }
  /**
   * 等站点信息,但最多等 capMs:第一次进页面前用(router 的守卫),让品牌和主题色先于首屏就位;
   * 接口慢就不再等,先出页面(这期间品牌位空着,见 ui/brand-identity.ts),信息到了页面自己更新。
   */
  function ready(capMs: number): Promise<void> {
    if (settled.value) return Promise.resolve();
    return Promise.race([load(), new Promise<void>((resolve) => setTimeout(resolve, capMs))]);
  }

  const banner = computed(() => {
    const t = info.value;
    if (!t || t.status === 'active') return null;
    if (t.status === 'grace')
      return `${t.name ?? '机构'}套餐已于 ${day(t.expiresAt)} 到期,当前为只读(宽限期至 ${day(t.graceUntil)})。学生可看不可学,不能新建考试;已发布的考试按原计划进行。`;
    if (t.status === 'quota_readonly') return '学员数已达配额,暂不能新增学员;已有学员正常使用。';
    if (t.status === 'migrating')
      return `${t.name ?? '机构'}正在迁移到私有化部署,迁移期间为只读;已发布的考试按原计划进行。`;
    if (t.status === 'migrated')
      return `${t.name ?? '机构'}已迁移到私有化部署,这里的数据只读保留${
        t.transfer?.retainUntil ? `到 ${day(t.transfer.retainUntil)}` : ''
      };请到私有化系统里继续使用。`;
    return `${t.name ?? '机构'}服务已暂停,请联系机构管理员;已发布的考试按原计划进行。`;
  });
  /** 冻结 / 待删除:机构后台只留续费、下载中心与考中考后的入口(10-02 口径 5) */
  const suspended = computed(
    () => info.value?.status === 'frozen' || info.value?.status === 'pending_delete',
  );
  /** 私有化部署的实例(模块 / 配额 / 到期由授权文件决定;没有合作机构、分成、在线采购) */
  const isPrivate = computed(() => deploy.value?.mode === 'private');
  /** 机构显示名(没有 = 用皮肤的平台品牌名) */
  const brandName = computed(() => info.value?.brand?.displayName ?? null);
  const logoUrl = computed(() => info.value?.brand?.logoUrl ?? null);
  /** 公立学校站点:后台显示「公立学校模式」、不出付费功能(基线 20) */
  const isSchool = computed(() => info.value?.type === 'public_school');
  /** 模块开没开:平台站点 / 没拿到 = 不限;到期只读的算开着(能看) */
  function hasModule(key: string): boolean {
    const m = info.value?.modules;
    return !m || m.includes(key);
  }
  function moduleExpired(key: string): boolean {
    return !!info.value?.expiredModules?.includes(key);
  }

  return {
    info,
    deploy,
    isPrivate,
    surface,
    banner,
    suspended,
    brandName,
    logoUrl,
    isSchool,
    settled,
    load,
    ready,
    faceOf,
    applySurface,
    hasModule,
    moduleExpired,
  };
});
